import { ImagePlus, MessageSquareWarning, Send } from 'lucide-react'
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { Button } from '../Button/Button'
import { FileInput } from '../FileDropzone/FileDropzone'
import { Field } from '../Field/Field'
import { IconButton } from '../IconButton/IconButton'
import { InfoNote } from '../InfoNote/InfoNote'
import { Modal } from '../Modal/Modal'
import { SegmentedControl } from '../SegmentedControl/SegmentedControl'
import { SwitchField } from '../SwitchField/SwitchField'
import { Textarea } from '../Textarea/Textarea'
import { useToast } from '../Toast/Toast'
import s from './ReportProblem.module.css'
import { InsideModal, setReportSlot } from './report-slot'

export type ReportKind = 'problem' | 'suggestion'

export interface ProblemReport {
  kind: ReportKind
  message: string
  /** La page telle qu'elle était à l'ouverture du ticket, en URL `data:` — `null` sans capture. */
  screenshot: string | null
  /** Les images que la personne ajoute elle-même, en plus de la capture. */
  images: File[]
}

/**
 * Le bouton « Signaler un problème » et son ticket (#481), sur tous les écrans des deux apps.
 *
 * Présentationnel (règle du paquet) : photographier la page et envoyer le ticket appartiennent à
 * l'app, qui les passe en props. `onSend` dit si le ticket est parti ou s'il attend le réseau.
 *
 * La capture se prend **avant** l'ouverture de la boîte, sinon elle montrerait la boîte. Elle est
 * montrée avant l'envoi, et se retire : elle peut montrer un nom, une adresse, une photo (D15).
 * Une capture ratée n'empêche pas le ticket (M21). D'autres images s'ajoutent à la main : une photo
 * du terrain, l'écran d'avant.
 *
 * Sans `trigger`, le bouton flotte en bas à droite et porte `data-report-trigger`, que la capture
 * de l'app écarte de l'image. `trigger` le range ailleurs — une entrée de la navigation, là où un
 * bouton flottant recouvrirait une action.
 *
 * Une boîte ouverte recouvre l'un comme l'autre : celui de l'app s'inscrit donc aussi pour que
 * chaque `Modal` en monte un dans son en-tête (`report-slot.ts`).
 */
export interface ReportProblemProps {
  capture: () => Promise<string | null>
  onSend: (report: ProblemReport) => Promise<'sent' | 'queued'>
  trigger?: (props: { onClick: () => void; disabled: boolean }) => ReactNode
}

export function ReportProblem({ capture, onSend, trigger }: ReportProblemProps) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [kind, setKind] = useState<ReportKind>('problem')
  const [message, setMessage] = useState('')
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [attach, setAttach] = useState(true)
  const [images, setImages] = useState<File[]>([])
  const [sending, setSending] = useState(false)
  const picker = useRef<HTMLInputElement>(null)
  const insideModal = useContext(InsideModal)

  useEffect(() => {
    if (insideModal) return
    setReportSlot((nested) => <ReportProblem capture={capture} onSend={onSend} trigger={nested} />)
    return () => setReportSlot(null)
  }, [insideModal, capture, onSend])

  const previews = useMemo(() => images.map((image) => URL.createObjectURL(image)), [images])
  useEffect(() => () => previews.forEach((url) => URL.revokeObjectURL(url)), [previews])

  async function start() {
    setCapturing(true)
    const shot = await capture().catch(() => null)
    setScreenshot(shot)
    setAttach(true)
    setImages([])
    setCapturing(false)
    setOpen(true)
  }

  async function send() {
    setSending(true)
    const outcome = await onSend({
      kind,
      message: message.trim(),
      screenshot: attach ? screenshot : null,
      images,
    })
    setSending(false)
    setOpen(false)
    setKind('problem')
    setMessage('')
    setImages([])
    toast(
      outcome === 'sent' ? 'Ticket envoyé' : 'Ticket enregistré, il partira au retour du réseau',
    )
  }

  return (
    <>
      {trigger === undefined ? (
        <IconButton
          label="Signaler un problème"
          title="Signaler un problème"
          className={s.trigger}
          disabled={capturing}
          onClick={() => void start()}
          data-report-trigger=""
        >
          <MessageSquareWarning size={20} aria-hidden />
        </IconButton>
      ) : (
        trigger({ onClick: () => void start(), disabled: capturing })
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        reportable={false}
        icon={<MessageSquareWarning size={20} />}
        title="Signaler un problème"
        busy={sending}
        footer={
          <>
            <Button tone="ghost" disabled={sending} onClick={() => setOpen(false)}>
              Annuler
            </Button>
            <Button
              tone="brand"
              disabled={message.trim() === '' || sending}
              onClick={() => void send()}
            >
              <Send size={16} aria-hidden />
              Envoyer
            </Button>
          </>
        }
      >
        <div className={s.body}>
          <SegmentedControl
            label="Type de ticket"
            value={kind}
            onValueChange={setKind}
            options={[
              { value: 'problem', label: 'Un problème' },
              { value: 'suggestion', label: 'Une suggestion' },
            ]}
          />
          <Field label={kind === 'problem' ? "Ce qui s'est passé" : 'La suggestion'}>
            {({ id, labelId, hintId }) => (
              <Textarea
                id={id}
                aria-labelledby={labelId}
                aria-describedby={hintId}
                rows={4}
                value={message}
                onValueChange={setMessage}
                placeholder={
                  kind === 'problem'
                    ? "Ce que vous faisiez, ce qui s'est passé, ce que vous attendiez."
                    : 'Ce qui vous aiderait, et sur quel écran.'
                }
              />
            )}
          </Field>
          {screenshot === null ? (
            <InfoNote>
              La capture de l'écran n'a pas pu se faire : le ticket part sans elle.
            </InfoNote>
          ) : (
            <>
              {attach && (
                <img className={s.shot} src={screenshot} alt="La capture qui part avec le ticket" />
              )}
              <SwitchField
                label="Joindre la capture de l'écran"
                hint="Elle peut montrer un nom, une adresse ou une photo : vérifiez-la avant l'envoi."
                checked={attach}
                onCheckedChange={setAttach}
              />
            </>
          )}
          {images.map((image, index) => (
            <div key={previews[index]} className={s.image}>
              <img className={s.shot} src={previews[index]} alt={`Image ajoutée ${index + 1}`} />
              <Button
                tone="ghost"
                size="sm"
                onClick={() => setImages(images.filter((other) => other !== image))}
              >
                Retirer l'image
              </Button>
            </div>
          ))}
          <div>
            <Button size="sm" onClick={() => picker.current?.click()}>
              <ImagePlus size={16} aria-hidden />
              Ajouter une image
            </Button>
            <FileInput
              ref={picker}
              accept="image/*"
              multiple
              hidden
              aria-label="Images à joindre"
              onFiles={(added) => setImages([...images, ...added])}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}
